import React, { useState, useEffect } from 'react';
import { Spinner, Alert } from 'reactstrap';
import {
    Briefcase, User, Calendar, Building2, Download, FileText, CheckCircle, ChevronDown, ChevronUp, ScrollText, MapPin, DollarSign
} from 'lucide-react';
import { API_URL } from '../utils/Constants/URLS';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Logo from '../../images/greenergy-logo.png';
import { addOHSEGuidelinesToPDF } from './OHSEGuidelines';

const ViewWorkOrder = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchWorkOrders();
    }, []);

    const fetchWorkOrders = async () => {
        try {
            const response = await fetch(`${API_URL}work-orders`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            const result = await response.json();
            if (result.error) throw new Error(result.message);
            setData(result.data?.docs || result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    const totalWOValue = (workItems) =>
        workItems?.reduce((sum, item) => sum + (item.totalValue || 0), 0) || 0;

    const handleDownloadPDF = (wo) => {
        const doc = new jsPDF();
        const companyLogo = Logo;

        if (companyLogo) {
            doc.addImage(companyLogo, 'PNG', 14, 10, 40, 20);
        } else {
            doc.setFontSize(18).setFont("helvetica", "bold").setTextColor(37, 99, 235);
            doc.text("GREENERGY SOLUTION", 14, 20);
        }

        doc.setFontSize(20).setTextColor(17, 24, 39).setFont("helvetica", "bold");
        doc.text("WORK ORDER", 196, 20, { align: "right" });

        doc.setFontSize(10).setFont("helvetica", "normal").setTextColor(100);
        doc.text(`WO Number: ${wo.workOrderNumber || 'N/A'}`, 196, 28, { align: "right" });
        doc.text(`Date: ${formatDate(wo.createdAt)}`, 196, 33, { align: "right" });

        doc.setDrawColor(229, 231, 235).line(14, 40, 196, 40);

        // Address Section
        doc.setFontSize(11).setTextColor(0).setFont("helvetica", "bold").text("Purchaser:", 14, 50);
        doc.setFont("helvetica", "normal").setFontSize(9);
        doc.text(["Greenergy Solution Private Limited", "Siliguri, West Bengal - 734001", "GSTIN: 19AAECG1743K1ZG"], 14, 56);

        doc.setFontSize(11).setFont("helvetica", "bold").text("Vendor Details:", 110, 50);
        doc.setFont("helvetica", "normal").setFontSize(9);
        doc.text([
            wo.vendor?.vendorName || 'N/A',
            doc.splitTextToSize(`Address: ${wo.vendor?.address || 'N/A'}`, 80)
        ].flat(), 110, 56);

        // Table
        const tableRows = wo.workItems?.map((item, index) => [
            index + 1,
            { content: item.description || '—', styles: { fontStyle: 'bold' } },
            item.unit || '—',
            item.rate?.toLocaleString('en-IN') || '0',
            item.totalArea || '0',
            item.totalValue?.toLocaleString('en-IN') || '0',
        ]) || [];

        autoTable(doc, {
            startY: 75,
            head: [['#', 'Item Description', 'Unit', 'Rate', 'Total Area', 'Total Value']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [67, 56, 202], fontSize: 10, halign: 'center' }, // Indigo-700
            styles: { fontSize: 9, cellPadding: 3 },
        });

        let finalY = doc.lastAutoTable.finalY + 10;

        // Total Value
        doc.setFillColor(249, 250, 251).rect(130, finalY, 66, 10, 'F');
        doc.setFont("helvetica", "bold").setFontSize(11);
        doc.text("Grand Total:", 135, finalY + 7);
        doc.text(`Rs. ${totalWOValue(wo.workItems).toLocaleString('en-IN')}`, 190, finalY + 7, { align: 'right' });

        finalY += 20;

        // Details Section (Scope, Exclusions, etc)
        const blockText = (title, text, yPos) => {
            doc.setFontSize(10).setFont("helvetica", "bold").setTextColor(17, 24, 39).text(title, 14, yPos);
            doc.setFontSize(9).setFont("helvetica", "normal").setTextColor(75, 85, 99);
            const splitText = doc.splitTextToSize(text || 'N/A', 180);
            doc.text(splitText, 14, yPos + 5);
            return yPos + 5 + (splitText.length * 4) + 5;
        };

        if (finalY > 250) { doc.addPage(); finalY = 20; }
        finalY = blockText("Scope of Work:", wo.scopeOfWork, finalY);

        if (finalY > 250) { doc.addPage(); finalY = 20; }
        finalY = blockText("Exclusions:", wo.exclusions, finalY);

        if (finalY > 250) { doc.addPage(); finalY = 20; }
        finalY = blockText("Site Address:", wo.siteAddress, finalY);

        if (finalY > 250) { doc.addPage(); finalY = 20; }
        finalY = blockText("Billing Address:", wo.billingAddress, finalY);
        
        if (finalY > 250) { doc.addPage(); finalY = 20; }
        finalY = blockText("Payment Schedule:", wo.paymentSchedule, finalY);

        if (finalY > 250) { doc.addPage(); finalY = 20; }
        finalY = blockText("Billing Schedule:", wo.billingSchedule, finalY);

        if (finalY > 250) { doc.addPage(); finalY = 20; }
        finalY = blockText("Extra Specifications:", wo.extraSpecifications, finalY);

        // Signature
        if (finalY > 250) { doc.addPage(); finalY = 20; }
        doc.setDrawColor(0).line(140, finalY + 40, 196, finalY + 40);
        doc.setFont("helvetica", "bold").setTextColor(17, 24, 39);
        doc.text("Authorized Signatory", 168, finalY + 45, { align: 'center' });

        addOHSEGuidelinesToPDF(doc, wo);

        doc.save(`WO_${wo.workOrderNumber || 'Document'}.pdf`);
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen gap-3">
            <Spinner color="primary" />
            <span className="text-gray-600 font-semibold">Loading Work Orders...</span>
        </div>
    );

    if (error) return <div className="p-6"><Alert color="danger">Failed to load: {error}</Alert></div>;

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-8 lg:px-12">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8 pb-5 border-b-2 border-gray-200">
                <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Work Orders</h1>
                    <p className="text-gray-500 text-sm mt-1">Track and manage all issued work orders and services</p>
                </div>
                <span className="bg-indigo-700 text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest">
                    {data.length} Orders
                </span>
            </div>

            <div className="flex flex-col gap-6">
                {data.map((wo) => (
                    <div key={wo._id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                        <div className="bg-indigo-900 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <Briefcase size={20} className="text-indigo-400" />
                                <span className="text-white font-bold text-lg tracking-tight">{wo.workOrderNumber || 'Draft WO'}</span>
                                <button onClick={() => handleDownloadPDF(wo)} className="ml-2 flex items-center gap-2 bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-30 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all">
                                    <FileText size={14} className="text-indigo-300" /> Download PDF
                                </button>
                            </div>
                            <div className="flex gap-6 text-indigo-200 text-sm">
                                <div className="flex items-center gap-2"><Calendar size={14} /> <span>Date:</span> <span className="text-white">{formatDate(wo.createdAt)}</span></div>
                                <div className="flex items-center gap-2"><CheckCircle size={14} /> <span>Status:</span> <span className="text-white capitalize">{wo.status}</span></div>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {/* Vendor Info */}
                                <div className="flex flex-col h-full">
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1 mb-3"><Building2 size={13} /> Vendor Details</p>
                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex-1">
                                        <p className="font-bold text-gray-900 text-base mb-1">{wo.vendor?.vendorName || 'N/A'}</p>
                                        <p className="text-gray-500 text-xs mb-1 break-all line-clamp-3">{wo.vendor?.address}</p>
                                    </div>
                                </div>

                                {/* Value Details */}
                                <div className="flex flex-col h-full">
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1 mb-3"><DollarSign size={13} /> Value Overview</p>
                                    <div className="bg-green-50 border border-green-100 rounded-xl p-4 flex-1 flex flex-col justify-center items-center">
                                        <p className="text-[10px] text-gray-400 uppercase mb-1 font-bold">Total Work Order Value</p>
                                        <p className="text-2xl font-extrabold text-green-700">₹ {totalWOValue(wo.workItems).toLocaleString('en-IN')}</p>
                                        <p className="text-xs text-green-600 mt-2 font-semibold">{wo.workItems?.length || 0} Work Items</p>
                                    </div>
                                </div>

                                {/* Setup & Schedules */}
                                <div className="flex flex-col h-full">
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1 mb-3"><ScrollText size={13} /> Schedules</p>
                                    <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 flex-1 text-xs space-y-3">
                                        <div>
                                            <span className="text-gray-500 block mb-0.5 font-semibold">Payment Schedule:</span>
                                            <span className="font-bold text-orange-900 whitespace-pre-wrap">{wo.paymentSchedule}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 block mb-0.5 font-semibold">Billing Schedule:</span>
                                            <span className="font-bold text-orange-900 whitespace-pre-wrap">{wo.billingSchedule}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Info toggles */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                <div className="flex flex-col">
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1 mb-2"><CheckCircle size={13} /> Scope of Work</p>
                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex-1 text-sm text-gray-700 h-24 overflow-y-auto custom-scrollbar whitespace-pre-wrap">{wo.scopeOfWork}</div>
                                </div>
                                <div className="flex flex-col">
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1 mb-2"><MapPin size={13} /> Site Address</p>
                                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex-1 text-sm text-gray-700 h-24 overflow-y-auto custom-scrollbar whitespace-pre-wrap">{wo.siteAddress}</div>
                                </div>
                            </div>

                            <div className="border-t border-dashed border-gray-200 my-6" />

                            {/* Work Items Toggle */}
                            <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleRow(wo._id)}>
                                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-2 mb-0">
                                    <Briefcase size={13} /> Work Items <span className="bg-indigo-900 text-white text-xs font-bold px-2 py-0.5 rounded-full">{wo.workItems?.length || 0}</span>
                                </p>
                                <button className="flex items-center gap-2 border border-indigo-200 bg-indigo-50 hover:bg-indigo-900 hover:text-white text-indigo-700 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all">
                                    {expandedRows[wo._id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {expandedRows[wo._id] ? 'Hide Details' : 'View All Details'}
                                </button>
                            </div>

                            {expandedRows[wo._id] && (
                                <div className="mt-4 rounded-xl overflow-hidden border border-indigo-100 overflow-x-auto shadow-sm">
                                    <table className="w-full text-sm">
                                        <thead className="bg-indigo-50 text-indigo-900 text-xs uppercase tracking-wider">
                                            <tr>{['#', 'Item Description', 'Unit', 'Rate (₹)', 'Total Area', 'Item Total (₹)'].map(h => <th key={h} className="px-4 py-3 font-bold">{h}</th>)}</tr>
                                        </thead>
                                        <tbody className="divide-y divide-indigo-50">
                                            {wo.workItems?.map((item, idx) => (
                                                <tr key={item._id} className="hover:bg-indigo-50/30 transition-colors">
                                                    <td className="px-4 py-4 text-gray-500 font-medium align-top">{idx + 1}</td>
                                                    <td className="px-4 py-4 align-top"><p className="font-bold text-gray-900 mb-0 max-w-sm whitespace-pre-wrap">{item.description}</p></td>
                                                    <td className="px-4 py-4 align-top"><span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">{item.unit || '—'}</span></td>
                                                    <td className="px-4 py-4 text-gray-600 font-medium align-top">{item.rate?.toLocaleString('en-IN') || 0}</td>
                                                    <td className="px-4 py-4 text-gray-600 font-medium align-top">{item.totalArea || 0}</td>
                                                    <td className="px-4 py-4 text-indigo-900 font-bold align-top">{item.totalValue?.toLocaleString('en-IN') || 0}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-indigo-50/50">
                                            <tr>
                                                <td colSpan="5" className="px-4 py-3 text-right font-bold text-indigo-900 text-xs uppercase tracking-widest">Total Valuation</td>
                                                <td className="px-4 py-3 font-extrabold text-indigo-900">₹ {totalWOValue(wo.workItems).toLocaleString('en-IN')}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                    
                                    <div className="bg-gray-50 p-6 border-t border-indigo-100 grid md:grid-cols-2 gap-6">
                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Billing Address</p>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{wo.billingAddress}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Exclusions</p>
                                            <p className="text-sm text-rose-600 whitespace-pre-wrap">{wo.exclusions}</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Extra Specifications</p>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{wo.extraSpecifications}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <User size={14} className="text-indigo-500" />
                                <span>Created by</span> <span className="font-semibold text-gray-800">{wo.createdBy?.name || 'Unknown'}</span>
                                {wo.createdBy?.role && <span className="bg-gray-200 text-gray-600 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">{wo.createdBy.role.replace(/_/g, ' ')}</span>}
                            </div>
                            <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">Created on: {formatDate(wo.createdAt)}</span>
                        </div>
                    </div>
                ))}
                
                {!loading && data.length === 0 && (
                    <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-sm">
                        <Briefcase size={48} className="mx-auto text-gray-300 mb-4" />
                        <h3 className="text-lg font-bold text-gray-900 mb-1">No Work Orders Found</h3>
                        <p className="text-gray-500">There are no work orders available to display at this time.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewWorkOrder;
